/**
 * Dynamic Page Component
 * Renders full pages with hero and content blocks
 */

import { BlockRenderer } from '../blocks/renderer.js';

export class DynamicPage {
  #container = null;
  #currentPage = null;
  #abortController = new AbortController();

  constructor(containerId) {
    this.#container = document.getElementById(containerId);
  }

  render(pageData) {
    if (!this.#container || !pageData) return;
    
    this.#currentPage = pageData;
    
    const { slug, title, metaDescription, hero, blocks } = pageData;
    
    // Build HTML
    this.#container.innerHTML = `
      <article class="dynamic-page page-${slug}">
        ${hero?.enabled ? this.renderHero(hero) : ''}
        <main class="page-content">
          ${blocks?.filter(b => b.isActive).sort((a, b) => a.sortOrder - b.sortOrder).map(block => 
            BlockRenderer.render(block)
          ).join('')}
        </main>
      </article>
    `;
    
    // Initialize animations
    this.initializeAnimations();
    
    // Scroll to top
    window.scrollTo(0, 0);
  }

  renderHero(hero) {
    const { title, subtitle, image, overlayOpacity = 50, alignment = 'center', cta } = hero;
    
    return `
      <section class="page-hero align-${alignment}" style="--overlay-opacity: ${overlayOpacity / 100}">
        ${image ? `
          <div class="hero-background">
            <img src="${image}" alt="" loading="eager">
            <div class="hero-overlay"></div>
          </div>
        ` : ''}
        <div class="hero-content">
          <h1 class="hero-title">${title}</h1>
          ${subtitle ? `<p class="hero-subtitle">${subtitle}</p>` : ''}
          ${cta?.text ? `<a href="${cta.url}" class="hero-cta btn btn-primary">${cta.text}</a>` : ''}
        </div>
      </section>
    `;
  }

  initializeAnimations() {
    const signal = this.#abortController.signal;
    
    // Intersection Observer for block animations
    const blocks = this.#container.querySelectorAll('.block-cards, .block-stats, .block-team, .block-timeline');
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          
          // Trigger card animations
          const cards = entry.target.querySelectorAll('.feature-card, .stat-item, .team-member, .timeline-item');
          cards.forEach((card, i) => {
            setTimeout(() => card.classList.add('visible'), i * 100);
          });
        }
      });
    }, { threshold: 0.1, signal });

    blocks.forEach(block => observer.observe(block));
    
    // Count-up animation for stats
    const statValues = this.#container.querySelectorAll('.stat-value[data-count]');
    statValues.forEach(stat => {
      const target = parseInt(stat.dataset.count);
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.animateCount(stat, target);
          observer.disconnect();
        }
      });
      observer.observe(stat);
    });
  }

  animateCount(element, target) {
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const prefix = element.textContent.match(/^[^0-9]*/)?.[0] || '';
    const suffix = element.textContent.match(/[^0-9]*$/)?.[0] || '';
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
      }
      element.textContent = prefix + Math.floor(current).toLocaleString() + suffix;
    }, duration / steps);
  }

  hide() {
    if (this.#container) {
      this.#container.style.display = 'none';
    }
  }

  show() {
    if (this.#container) {
      this.#container.style.display = 'block';
    }
  }

  destroy() {
    this.#abortController.abort();
    if (this.#container) {
      this.#container.innerHTML = '';
    }
  }
}
